import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AssetsService, GeographyOption } from '../../../core/services/assets.service';

@Component({
  selector: 'app-state-select, app-region-select, app-region-autocomplete-input',
  standalone: false,
  templateUrl: './region-select.html',
  styleUrl: './region-select.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RegionSelect), multi: true }],
})
export class RegionSelect implements ControlValueAccessor, OnChanges {
  /** ISO-2 or ISO-3 country code. `countryId` remains as a temporary template alias. */
  @Input() countryIso = '';
  @Input() countryId = '';
  @Input() placeholder = 'Search state or region';
  @Input() selectClass = 'form-select';
  @Output() stateChange = new EventEmitter<GeographyOption | null>();
  @Output() regionChange = new EventEmitter<GeographyOption | null>();

  readonly control = new FormControl('', { nonNullable: true });
  readonly search = (query: string) => this.load(query);
  readonly preload = (values: string[]) => Promise.all(values.filter(Boolean).map((value) => this.find(value)));
  options: GeographyOption[] = [];
  private savedValue = '';
  private onChange = (_value: string) => {};
  private onTouched = () => {};
  private disabled = false;
  private loadSequence = 0;

  constructor(private readonly assets: AssetsService) {
    this.control.valueChanges.subscribe((value) => this.select(value));
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes['countryIso'] && !changes['countryId']) return;
    const country = this.country();
    const sequence = ++this.loadSequence;
    this.options = [];
    if (!country) {
      this.control.setValue('', { emitEvent: false });
      this.syncDisabled();
      return;
    }
    const options = await this.assets.getStates(country);
    if (sequence !== this.loadSequence || country !== this.country()) return;
    this.options = options;
    const found = this.options.some((option) => option.value === this.savedValue);
    if (this.savedValue && !found) this.control.setValue('', { emitEvent: false });
    this.syncDisabled();
  }

  writeValue(value: string | null): void {
    this.savedValue = String(value || '').toUpperCase();
    this.control.setValue(this.savedValue, { emitEvent: false });
  }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; this.syncDisabled(); }
  touched(): void { this.onTouched(); }

  private async load(query: string): Promise<GeographyOption[]> {
    const country = this.country();
    if (!country) return [];
    const sequence = this.loadSequence;
    const options = await this.assets.getStates(country, query);
    if (sequence !== this.loadSequence || country !== this.country()) return [];
    this.options = mergeOptions(this.options, options);
    return options;
  }

  private async find(value: string): Promise<GeographyOption> {
    const option = this.options.find((candidate) => candidate.value === value);
    if (option) return option;
    await this.load('');
    const loaded = this.options.find((candidate) => candidate.value === value);
    if (!loaded) throw new Error(`State '${value}' was not found for the selected country.`);
    return loaded;
  }

  private select(value: string): void {
    const selected = this.options.find((option) => option.value === value) ?? null;
    this.savedValue = selected?.value || '';
    this.onChange(this.savedValue);
    this.stateChange.emit(selected);
    this.regionChange.emit(selected);
  }

  private country(): string { return String(this.countryIso || this.countryId || '').trim().toUpperCase(); }
  private syncDisabled(): void {
    this.disabled || !this.country()
      ? this.control.disable({ emitEvent: false })
      : this.control.enable({ emitEvent: false });
  }
}

function mergeOptions(current: GeographyOption[], incoming: GeographyOption[]): GeographyOption[] {
  return [...new Map([...current, ...incoming].map((option) => [option.value, option])).values()];
}
